import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, KeyRound, LayoutDashboard, LifeBuoy, UserRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";

function dashboardFor(user) {
  if (user?.role === "admin") return "/admin";
  if (user?.role === "brand" || user?.role === "client") return "/client";
  return "/dashboard";
}

function roleLabel(user) {
  if (user?.role === "admin") return "Admin";
  if (user?.role === "brand" || user?.role === "client") return "Client Hub";
  return "Dashboard";
}

export default function AccountMenu() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  const dashboardPath = dashboardFor(user);
  const supportPath = user?.role === "admin" ? "/admin/support" : "/support";

  const resetPassword = async () => {
    setLoadingReset(true);
    try {
      const { data } = await api.post("/auth/password-reset/me");
      toast.success(data.message || "Link reset password sudah dikirim ke email.");
      setOpen(false);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Gagal mengirim reset password");
    } finally {
      setLoadingReset(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        data-testid="header-account-toggle"
        className="nb-btn nb-btn-secondary min-w-10 text-xs px-3 py-2 sm:px-4"
        aria-expanded={open}
        aria-label={roleLabel(user)}
      >
        <LayoutDashboard size={15} className="sm:hidden" />
        <span className="hidden sm:inline">{roleLabel(user)}</span>
        <ChevronDown size={14} />
      </button>

      {open && (
        <div className="fixed left-3 right-3 top-[4.75rem] z-50 rounded-xl border-2 border-[#0A0A0A] bg-[#FDFBF7] p-3 shadow-[4px_4px_0_0_#0A0A0A] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-3 sm:w-72">
          <div className="px-2 pb-3 border-b-2 border-[#0A0A0A]/10">
            <div className="flex items-center gap-2">
              <span className="nb-tag bg-[#D4FF00]">{roleLabel(user)}</span>
              {user?.is_super_admin && <span className="nb-tag bg-white">Super</span>}
            </div>
            <div className="font-bold mt-2 truncate">{user?.name}</div>
            <div className="text-xs text-[#4A4A4A] truncate">{user?.email}</div>
          </div>

          <div className="grid gap-2 pt-3">
            <MenuLink to={dashboardPath} icon={<LayoutDashboard size={15} />} onClick={() => setOpen(false)}>
              Dashboard
            </MenuLink>
            <MenuLink to="/profile" icon={<UserRound size={15} />} onClick={() => setOpen(false)}>
              Data diri & profile
            </MenuLink>
            <MenuLink to={supportPath} icon={<LifeBuoy size={15} />} onClick={() => setOpen(false)}>
              Bantuan chat
            </MenuLink>
            <button
              type="button"
              onClick={resetPassword}
              disabled={loadingReset}
              className="flex items-center gap-2 rounded-lg border-2 border-[#0A0A0A] bg-white px-3 py-2 text-left text-sm font-bold hover:bg-[#D4FF00] disabled:opacity-60"
            >
              <KeyRound size={15} />
              {loadingReset ? "Mengirim link..." : "Reset password"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuLink({ to, icon, children, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-2 rounded-lg border-2 border-[#0A0A0A] bg-white px-3 py-2 text-sm font-bold hover:bg-[#D4FF00]"
    >
      {icon}
      {children}
    </Link>
  );
}
